import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import { CLIENT_STATUTS, PAIEMENT_STATUTS, CONTRAT_STATUTS, fmtXOF, fmtDate } from "../lib/format";
import { ArrowLeft, Phone, Mail, MapPin, Link2, Copy, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import DocumentsPanel from "../components/DocumentsPanel";
import CNIPanel from "../components/CNIPanel";
import ClientEditDialog from "../components/ClientEditDialog";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [accessUrl, setAccessUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const canShare = ["admin", "secretaire", "commercial"].includes(user?.role);
  const canEdit = ["admin", "secretaire", "commercial"].includes(user?.role);

  const reload = async () => {
    const { data } = await api.get(`/clients/${id}`);
    setData(data);
  };

  useEffect(() => { reload(); }, [id]); // eslint-disable-line

  const generateLink = async () => {
    try {
      const { data } = await api.post(`/clients/${id}/access-link`);
      setAccessUrl(data.url);
      await navigator.clipboard.writeText(data.url).catch(() => {});
      setCopied(true);
      toast.success("Lien d'accès copié dans le presse-papier");
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (!data) return <Layout><div className="p-8 text-[#4B5775]">Chargement…</div></Layout>;

  const displayName = data.type === "entreprise" ? data.raison_sociale : `${data.nom} ${data.prenom || ""}`.trim();

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-[1500px] mx-auto">
        <Link to="/clients" className="inline-flex items-center gap-2 text-xs text-[#4B5775] hover:text-[#0F1A3D] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux clients
        </Link>
        <PageHeader
          kicker={data.type === "entreprise" ? "Entreprise" : "Particulier"}
          title={displayName}
          subtitle={<span className="inline-flex items-center gap-3"><StatusPill statut={data.statut} map={CLIENT_STATUTS} /></span>}
          action={(
            <div className="flex items-center gap-2">
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E8EBFA] text-sm font-bold uppercase tracking-wider transition"
                  data-testid="edit-client-btn"
                >
                  <Pencil className="w-3.5 h-3.5" /> Modifier
                </button>
              )}
              {canShare && (
                <button
                  data-testid="generate-access-link-btn"
                  onClick={generateLink}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-sm font-semibold transition"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {copied ? "Lien copié !" : "Lien d'accès client"}
                </button>
              )}
            </div>
          )}
        />
        <ClientEditDialog open={editing} onClose={() => setEditing(false)} client={data} onUpdated={reload} />

        {accessUrl && (
          <div className="tpco-card p-4 mb-6 bg-[#FCE4EC] border-[#F8BBD0]" data-testid="access-link-banner">
            <div className="flex items-start gap-3">
              <Link2 className="w-5 h-5 text-[#E91E63] mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0F1A3D] mb-1">Lien d'accès client</div>
                <div className="font-mono text-xs text-[#4B5775] bg-white border border-[#F8BBD0] rounded px-2 py-1.5 break-all">{accessUrl}</div>
                <div className="text-[11px] text-[#4B5775] mt-2">Partagez ce lien par WhatsApp, SMS ou email. Le client pourra signer ses contrats et suivre ses paiements.</div>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(accessUrl); toast.success("Copié"); }}
                className="p-2 rounded-md hover:bg-white text-[#E91E63]"
                title="Copier"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="tpco-card p-6">
            <div className="label-tiny mb-3">Contact</div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2.5 text-[#0F1A3D]"><Phone className="w-4 h-4 text-[#1E3A8A]" /> <span className="font-mono">{data.telephone}</span></div>
              {data.telephone_2 && <div className="flex items-center gap-2.5 text-[#4B5775]"><Phone className="w-4 h-4 text-[#9CA3B5]" /> <span className="font-mono">{data.telephone_2}</span></div>}
              <div className="flex items-center gap-2.5 text-[#4B5775]"><Mail className="w-4 h-4 text-[#1E3A8A]" /> {data.email || "—"}</div>
              <div className="flex items-center gap-2.5 text-[#4B5775]"><MapPin className="w-4 h-4 text-[#1E3A8A]" /> {data.adresse || "—"}</div>
            </div>
          </div>
          <div className="tpco-card p-6">
            <div className="label-tiny mb-3">Total dû</div>
            <div className="font-display text-3xl font-bold text-[#0F1A3D]">{fmtXOF(data.stats.total_du)}</div>
            <div className="text-xs text-[#4B5775] mt-2">{data.stats.nb_contrats} contrat{data.stats.nb_contrats > 1 ? "s" : ""}</div>
          </div>
          <div className="tpco-card p-6">
            <div className="label-tiny mb-3">Solde impayé</div>
            <div className={`font-display text-3xl font-bold ${data.stats.solde > 0 ? "text-[#C83B3B]" : "text-[#2D5A3C]"}`}>{fmtXOF(data.stats.solde)}</div>
            <div className="text-xs text-[#4B5775] mt-2">Reçu : {fmtXOF(data.stats.total_paye)}</div>
          </div>
        </div>

        <div className="tpco-card overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-[#E2E5F1]"><h3 className="font-display text-lg font-bold">Contrats</h3></div>
          {data.contrats.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#9CA3B5]">Aucun contrat</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3FB] text-[#4B5775] text-xs uppercase">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Référence</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Date début</th>
                  <th className="text-right px-4 py-3 font-semibold">Montant</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E5F1]">
                {data.contrats.map((c) => (
                  <tr key={c.id} className="hover:bg-[#F8FAFC]">
                    <td className="px-6 py-3 font-mono text-xs">
                      <Link to={`/contrats/${c.id}`} className="text-[#1E3A8A] hover:underline">{c.reference}</Link>
                    </td>
                    <td className="px-4 py-3 capitalize">{c.type}</td>
                    <td className="px-4 py-3 text-[#4B5775]">{fmtDate(c.date_debut)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtXOF(c.type === "vente" ? c.prix_total : c.loyer_mensuel)}</td>
                    <td className="px-4 py-3"><StatusPill statut={c.statut} map={CONTRAT_STATUTS} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="tpco-card overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E5F1]"><h3 className="font-display text-lg font-bold">Paiements</h3></div>
          {data.paiements.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#9CA3B5]">Aucun paiement</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3FB] text-[#4B5775] text-xs uppercase">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">N°</th>
                  <th className="text-left px-4 py-3 font-semibold">Échéance</th>
                  <th className="text-right px-4 py-3 font-semibold">Montant</th>
                  <th className="text-right px-4 py-3 font-semibold">Reçu</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E5F1]">
                {data.paiements.map((p) => (
                  <tr key={p.id} className="hover:bg-[#F8FAFC]">
                    <td className="px-6 py-3 font-mono text-xs">#{p.numero}</td>
                    <td className="px-4 py-3 text-[#4B5775]">{fmtDate(p.date_echeance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtXOF(p.montant)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtXOF(p.montant_recu)}</td>
                    <td className="px-4 py-3"><StatusPill statut={p.statut} map={PAIEMENT_STATUTS} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6">
          <CNIPanel clientId={id} client={data} />
        </div>

        <div className="mt-6">
          <DocumentsPanel ownerType="client" ownerId={id} title="Autres pièces jointes" />
        </div>
      </div>
    </Layout>
  );
}
